import React, { useCallback, useRef, useState } from 'react';
import type { ContractTemplateAssignment } from 'global-intern-types';
import { useQuery } from '@apollo/client';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PagePathname } from '../../../../routes/paths';
import { GET_CONTRACT_TEMPLATE } from '../../api/contractTemplates';
import VertragsvorlageFormular, { VertragsvorlageAnfang } from '../components/VertragsvorlageFormular';
import { leeresDokument } from '../model/erzeugen';
import { leseGespeichertesVertragsdokument } from '../model/lesen';

export const VORLAGE_NICHT_LESBAR =
  'Diese Vertragsvorlage kann nicht geöffnet werden, weil ihr Inhalt nicht lesbar ist.';

const RUECKSPRUNG = `${PagePathname.briefvorlagen}?bereich=vertragsvorlagen`;

interface Antwort {
  getContractTemplate: {
    id: number;
    name: string;
    description: string | null;
    document: unknown;
    assignment?: ContractTemplateAssignment | null;
    updated_at?: string | null;
  } | null;
}

function ladeKennung(routeId: string | null): number | null | undefined {
  if (routeId === null) return null;
  if (!/^\d+$/.test(routeId)) return undefined;
  const id = Number(routeId);
  return Number.isSafeInteger(id) && id > 0 && id <= 2147483647 ? id : undefined;
}

interface SitzungProps {
  routeId: string | null;
  kopieId: string | null;
  onAngelegt: (id: number) => void;
  /** RITA-1541: tauscht die Sitzung aus und lädt den aktuellen gespeicherten Stand. */
  onNeuLaden: () => void;
}

function Vorlagensitzung({ routeId, kopieId, onAngelegt, onNeuLaden }: SitzungProps) {
  const navigate = useNavigate();
  const [istKopie] = useState(kopieId !== null);
  const [ladeId] = useState(() => ladeKennung(kopieId ?? routeId));
  const { data, loading, error } = useQuery<Antwort>(GET_CONTRACT_TEMPLATE, {
    variables: { id: ladeId ?? 0 },
    skip: typeof ladeId !== 'number',
    fetchPolicy: 'network-only',
  });
  const [neu] = useState<VertragsvorlageAnfang | null>(() =>
    ladeId === null
      ? { anfang: leeresDokument(), start: { id: null, name: '', beschreibung: '' }, unbekanntePlatzhalter: [] }
      : null,
  );
  const uebernommen = useRef<VertragsvorlageAnfang | null>(neu);
  // Spaetere Cacheantworten duerfen einen bereits begonnenen Entwurf nicht ersetzen.
  if (uebernommen.current === null && !loading && !error && data?.getContractTemplate) {
    const vorlage = data.getContractTemplate;
    if (vorlage.id === ladeId) {
      try {
        const gelesen = leseGespeichertesVertragsdokument(vorlage.document);
        uebernommen.current = {
          anfang: gelesen.dokument,
          start: {
            id: istKopie ? null : vorlage.id,
            name: istKopie ? `Kopie von ${vorlage.name}`.slice(0, 120) : vorlage.name,
            kopie: istKopie,
            beschreibung: vorlage.description ?? '',
            assignment: vorlage.assignment,
            updatedAt: istKopie ? null : (vorlage.updated_at ?? null),
          },
          unbekanntePlatzhalter: gelesen.unbekanntePlatzhalter,
        };
      } catch {
        // Alte, fremde und beschaedigte Dokumente erhalten dieselbe fachliche Meldung.
      }
    }
  }
  if (uebernommen.current) {
    return (
      <VertragsvorlageFormular
        anfang={uebernommen.current.anfang}
        start={uebernommen.current.start}
        unbekanntePlatzhalter={uebernommen.current.unbekanntePlatzhalter}
        onAngelegt={onAngelegt}
        onNeuLaden={onNeuLaden}
      />
    );
  }
  if (loading && typeof ladeId === 'number') {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress aria-label="Vorlage wird geladen" />
      </Box>
    );
  }
  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error" sx={{ mb: 2 }}>
        {VORLAGE_NICHT_LESBAR}
      </Alert>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(RUECKSPRUNG)}>
        Zurück zur Übersicht
      </Button>
    </Box>
  );
}

export default function VertragsvorlagePage() {
  const { id } = useParams<{ id: string }>();
  const routeId = id === undefined || id === 'neu' ? null : id;
  const [parameter] = useSearchParams();
  const kopieId = routeId === null ? parameter.get('kopie') : null;
  const routeSchluessel = JSON.stringify([routeId, kopieId]);
  const navigate = useNavigate();
  const sitzung = useRef({ nummer: 0, routeSchluessel, selbstAngelegt: null as string | null });
  if (sitzung.current.routeSchluessel !== routeSchluessel) {
    if (sitzung.current.selbstAngelegt === null || routeId !== sitzung.current.selbstAngelegt) {
      sitzung.current.nummer += 1;
    }
    sitzung.current.routeSchluessel = routeSchluessel;
    sitzung.current.selbstAngelegt = null;
  }
  const onAngelegt = (vorlagenId: number) => {
    sitzung.current.selbstAngelegt = String(vorlagenId);
    navigate(PagePathname.vertragsvorlagenBearbeiten.replace(':id', String(vorlagenId)), { replace: true });
  };
  // RITA-1541: „Neu laden“ (Speicherwarnung, frei gewordene Vorlage, zweites Fenster) beginnt eine
  // neue Sitzung mit frischem Abruf; der bisherige Entwurf wird dabei bewusst verworfen.
  const [, neuGeladen] = useState(0);
  const onNeuLaden = useCallback(() => {
    sitzung.current.nummer += 1;
    neuGeladen((zaehler) => zaehler + 1);
  }, []);
  return (
    <Vorlagensitzung
      key={sitzung.current.nummer}
      routeId={routeId}
      kopieId={kopieId}
      onAngelegt={onAngelegt}
      onNeuLaden={onNeuLaden}
    />
  );
}
